import React, { useEffect, useCallback } from "react";
import { setAnalyticsConsent } from "../../analytics";
// @ts-ignore
import "./CookieConsentBanner.css";

function useCookieConsent(): [boolean, React.Dispatch<React.SetStateAction<boolean>>] {
  // Read the stored choice SYNCHRONOUSLY on first render. This avoids the
  // previous two-render dance (prerendered banner wiped at hydration, then
  // re-inserted after the effect), which caused a visible flash and a mobile
  // layout shift (CLS 0.104) when the banner appeared ~1s after hydration.
  const [shown, setShown] = React.useState<boolean>(() => {
    try {
      const saved =
        typeof window !== "undefined"
          ? (window as any).localStorage.getItem("cookie-consent")
          : null;
      return !(saved === "accepted" || saved === "declined");
    } catch {
      return true;
    }
  });

  // Keep a storage re-check effect for SSR/prerender safety: during
  // react-snap prerendering localStorage is unavailable and the banner is
  // rendered (matching the baked HTML). No state updates are needed in the
  // browser because the initializer already read localStorage.
  useEffect(() => {
    try {
      const saved =
        typeof window !== "undefined"
          ? (window as any).localStorage.getItem("cookie-consent")
          : null;
      if (saved === "accepted" || saved === "declined") {
        setShown(false);
      }
    } catch {
      /* storage unavailable - keep initial value */
    }
  }, []);

  return [shown, setShown];
}
function saveChoice(choice: "accepted" | "declined"): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem("cookie-consent", choice);
  } catch {
    /* ignore */
  }
}

export default function CookieConsentBanner(): JSX.Element | null {
  const [shown, setShown] = useCookieConsent();

  const handleAccept = useCallback(() => {
    saveChoice("accepted");
    setAnalyticsConsent("accepted");
    setShown(false);
  }, [setShown]);

  const handleDecline = useCallback(() => {
    saveChoice("declined");
    setAnalyticsConsent("declined");
    setShown(false);
  }, [setShown]);

  // Rules of Hooks: this hook must run on every render, before any
  // conditional return, otherwise React throws error #310
  // ("Rendered more hooks than during the previous render").
  useEffect(() => {
    if (!shown) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleDecline();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleDecline, shown]);

  if (!shown) {
    return null;
  }

  return (
    <div
      className="cookie-consent"
      role="region"
      aria-label="Cookie consent"
      aria-live="polite"
    >
      <div className="cookie-consent-card">
        <div className="cookie-consent-copy">
          <svg
            className="cookie-consent-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M20.9 13.3a8.9 8.9 0 1 1-9.2-9.2 3.5 3.5 0 0 0 4.2 4.2 3.5 3.5 0 0 0 5 5Z" />
            <circle cx="8.5" cy="9.2" r=".9" fill="currentColor" stroke="none" />
            <circle cx="12.8" cy="13.4" r=".9" fill="currentColor" stroke="none" />
            <circle cx="8.6" cy="15.2" r=".9" fill="currentColor" stroke="none" />
            <circle cx="14.2" cy="17.9" r=".9" fill="currentColor" stroke="none" />
          </svg>
          <p className="cookie-consent-text">
            We use essential cookies to run the site and, with your consent,
            analytics cookies to improve it.{" "}
            <a className="cookie-consent-link" href="/privacy-policy">
              Learn more about our cookie policy
            </a>
            .
          </p>
        </div>
        <div className="cookie-consent-actions">
          <button
            type="button"
            className="cookie-consent-btn cookie-consent-decline"
            onClick={handleDecline}
          >
            Reject non-essential
          </button>
          <button
            type="button"
            className="cookie-consent-btn cookie-consent-accept"
            onClick={handleAccept}
          >
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
}
